import { type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, IconButton } from '@socialchorus/shared-ui-components';
import Spinner from '../../../../components/ui/spinner';
import type { ContentImage } from '../../../../models/user-generated-contents/types';
import { MAX_IMAGES } from './use-image-uploader';
import { useSortableItem, SortableGrid } from './sortable';
import './images.scss';

type SortableImageItemProps = {
  image: ContentImage;
  index: number;
  onDelete: (index: number) => void;
};

const SortableImageItem = ({
  image,
  index,
  onDelete,
}: SortableImageItemProps) => {
  const { t } = useTranslation();
  const { setNodeRef, style, attributes, listeners } = useSortableItem(
    image.url
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="ugc-images__item"
      {...attributes}
      {...listeners}
    >
      <img
        src={image.url}
        alt={t('user_generated_content.media.images.image_preview', {
          index: index + 1,
        })}
        className="ugc-images__thumbnail"
        draggable="false"
      />
      <div
        className="ugc-images__item-overlay"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <IconButton
          iconName="delete"
          size="compact"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onDelete(index);
          }}
          aria-label={t('user_generated_content.media.images.delete')}
          className="bg-black-05"
        />
      </div>
    </div>
  );
};

type ImagesProps = {
  images: ContentImage[];
  inProgress: File[];
  totalCount: number;
  onDelete: (index: number) => void;
  onReorder: (images: ContentImage[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dragHandleProps?: Record<string, any>;
};

const Images = ({
  images,
  inProgress,
  totalCount,
  onDelete,
  onReorder,
  dragHandleProps,
}: ImagesProps) => {
  const { t } = useTranslation();

  return (
    <div className="ugc-images__section">
      <div
        className={`ugc-images__section-header${
          dragHandleProps ? ' ugc-images__section-header--draggable' : ''
        }`}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(dragHandleProps as any)}
      >
        <Badge
          text={t('user_generated_content.media.images.section_label', {
            count: totalCount,
            max: MAX_IMAGES,
          })}
        />
      </div>
      <SortableGrid
        items={images}
        getId={(img) => img.url}
        onReorder={(reordered) =>
          onReorder(
            reordered.map((img: ContentImage, i: number) => ({
              ...img,
              order: i,
            }))
          )
        }
      >
        <div className="ugc-images__grid">
          {images.map((image, i) => (
            <SortableImageItem
              key={image.url ?? image.id}
              image={image}
              index={i}
              onDelete={onDelete}
            />
          ))}
          {inProgress.map((file) => (
            <div
              key={`${file.name}-${file.lastModified}`}
              className="ugc-images__item ugc-images__item--uploading"
            >
              <Spinner loading />
              <span className="text-caption">{t('common.uploading')}</span>
            </div>
          ))}
        </div>
      </SortableGrid>
    </div>
  );
};

export default Images;
